import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { SOFT_SKILLS_RULES } from './stellar-soft-rules';

@Component({
  selector: 'stellar-soft',
  templateUrl: 'stellar-soft.component.html',
  styleUrls: ['stellar-soft.component.scss'],
})
export class StellarSoftComponent implements OnInit {

  @ViewChild('stellar', { static: false }) stellar: ElementRef;
  @HostListener('window:resize', ['$event'])
  onResize(event) { this.onWindowResize(); }

  @Input() set skills(vals) {
    this.patchSkills(vals);
  };
  @Input() disabled = false;
  @Output() skillsChanged = new EventEmitter<any>();

  loading = true;
  skillsForm = new FormArray([]);
  scale = 1;
  isMobile = false;
  verticalIntend = '0px auto';
  allSkills: any = [];
  private formPatched = false;
  private stellarLablesEl = null;

  constructor(
    private renderer: Renderer2, 
    private host: ElementRef,
    private translate: TranslateService
  ) {
    const modelSkills = Object.keys(SOFT_SKILLS_RULES.points).map(key => ({ name: key, ...SOFT_SKILLS_RULES.points[key] }));
    
    this.allSkills = this.addFormatColors(modelSkills);
  }

  ngOnInit(): void {
    this.createForm();
  }

  ngAfterViewInit() {
    this.onWindowResize();
  }

  private onWindowResize() {
    const divToMeasureWidth = this.stellar.nativeElement.parentElement.parentElement.offsetWidth;
    setTimeout(() => {
      if (divToMeasureWidth < 420) {
        this.scale = 1;
        this.isMobile = true
      } else {
        const scale = (Math.round((divToMeasureWidth / 740) * 100) + 5) / 100;
        this.verticalIntend = scale < 0.8 ? `0px auto` : `${Math.round(60 * scale)}px auto ${Math.round(60 * scale) + 5}px`;
        this.isMobile = false;
        this.scale = scale > 1 ? 1 : scale;
      }
    });
  }

  valueChanged(e, name) {
    const currentSkill = this.allSkills.find(item => item.name === name);
    const prevValue = currentSkill.score;
    const diffValue = Math.abs(Number(e.target.value) - prevValue);
    const upDown = Number(e.target.value) > prevValue ? 'up' : 'down';
    const tradeoffs = Object.keys(currentSkill.tradeoffs[upDown])
      .map(key => ({ name: key, value: currentSkill.tradeoffs[upDown][key] }))
      .filter(item => item.value !== 0);

    tradeoffs.map(rule => {
      const addValue = this.fixRounding(diffValue * rule.value);
      const toChangeIndex = (this.skillsForm as FormArray).controls.findIndex(item => item.get('name').value === rule.name);
      const controlToChange = (this.skillsForm as FormArray).at(toChangeIndex).get('score');
      const controlToChangeValue = this.fixRounding(Number(controlToChange.value));
      const valueToSet = this.fixRounding(controlToChangeValue + addValue);
      // const valueWithLimit = valueToSet >= 190 ? 190 : valueToSet <= 1 ? 1 : valueToSet;
      const valueWithLimit = valueToSet >= 100 ? 100 : valueToSet <= 1 ? 1 : valueToSet;
      controlToChange.setValue(valueWithLimit);
    })

    setTimeout(() => {
      const formNewVals = this.skillsForm.getRawValue();
      this.allSkills = this.allSkills.map(item => (
        { ...item, score: formNewVals.find(row => row.name === item.name)?.score }
      ));
      this.skillsChanged.emit(this.allSkills.map(item => ({ name: item.name, score: item.score })));
    })
  }

  getSkillValueByIndex(i) {
    return this.skillsForm.at(i).get('score') as FormControl;
  }

  private patchSkills(vals) {
    if (vals && vals.length && !this.formPatched) {
      setTimeout(() => {
        this.skillsForm.patchValue(vals);
        this.formPatched = true;
      });
    }
  }

  private createForm() {
    this.skillsForm.clear()
    this.allSkills.map((item: any) => {
      const group = new FormGroup({
        name: new FormControl(item.name),
        score: new FormControl(item.score),
        color: new FormControl(item.color)
      });
      this.skillsForm.push(group);
    });
    setTimeout(() => {
      this.loading = false;
      this.ellipse(this.allSkills.length, 290, 260, 0, 35, 'stellar-label-area', 'stellar-label');
    }, 100);
  }
  
  private ellipse(n, rx, ry, so, wh, idd, cls) {
    this.stellarLablesEl = this.renderer.createElement('div');
    this.renderer.setProperty(this.stellarLablesEl, 'id', idd);
    this.renderer.setStyle(this.stellarLablesEl, 'width', String((rx * 2) + wh) + 'px' );
    this.renderer.setStyle(this.stellarLablesEl, 'height', String((ry * 2) + wh) + 'px' );

    for (let i = 0; i < n; i++) {
      const label = this.renderer.createElement('div');
      this.renderer.addClass(label, cls);
      setTimeout(() => this.renderer.addClass(label, 'loaded'), i * 50);
      
      const translatedName = this.translate.instant('SoftSkills.' + this.allSkills[i].name);
      const translatedTooltip = this.translate.instant('SoftSkills.' + this.allSkills[i].name + 'Tooltip');

      this.renderer.setProperty(label, 'innerHTML', translatedName 
        + ' <i class="app-ico-mask app-ico-mask__info tooltip"></i>' 
        + '<div class="tooltiptext">' + translatedTooltip + '</div>')

      // this.renderer.setProperty(label, 'innerHTML', this.allSkills[i].name 
      //   + ' <i class="app-ico-mask app-ico-mask__info tooltip"></i>' 
      //   + '<div class="tooltiptext" style="top: ' 
      //   + String(ry + -ry * Math.cos((360 / n / 180) * (i + so) * Math.PI))  + 'px; left: ' 
      //   + String(rx + rx * (Math.sin((360 / n / 180) * (i + so) * Math.PI)) - 180) + 'px">' 
      //   + this.allSkills[i].tooltip + '</div>')

      this.renderer.setStyle(label, 'top', String(ry + -ry * Math.cos((360 / n / 180) * (i + so) * Math.PI)) + 'px');
      // this.renderer.setStyle(label, 'left', String(rx + rx * (Math.sin((360 / n / 180) * (i + so) * Math.PI)) - 100) + 'px');
      const angle = (360 / n / 180) * (i + so) * Math.PI;
      this.renderer.setStyle(label, 'top', String(ry - ry * Math.cos(angle)) + 'px');
      const isRight = Math.sin(angle) > 0;
      const offset = isRight ? rx * 0.27 : rx * 0.35;
      this.renderer.setStyle(label, 'left', String(rx + rx * Math.sin(angle) - offset) + 'px');

      this.renderer.appendChild(this.stellarLablesEl, label);
    }
    this.renderer.appendChild(this.stellar.nativeElement, this.stellarLablesEl);

    const elements = document.querySelectorAll('.tooltiptext');
    const stellar = document.getElementById('soft-skills-stellar')
    for (var i = 0; i < elements.length; i++) {
      elements[i].parentElement.onmouseover = (event) => {
        if (stellar && stellar.style) {
          stellar.style.zIndex = '1';
        }
      };
      elements[i].parentElement.onmouseleave = (event) => { 
        if (stellar && stellar.style) {
          stellar.style.zIndex = '2';
        }
      };
    }
  }

  private addFormatColors(arr) {
    if (!Array.isArray(arr)) { return []; }
    const defaultColorList = [
      '#FFBBBB', '#A3E0FF', '#FFD576', '#CEFF9B', '#FF8888', '#8FF0FF', '#FFA8E1', '#C6F8E3', '#D9AEFF', '#FFB571', '#C0C7FF'
    ];
    let colorList = defaultColorList;
    return arr.map(item => {
      if (!colorList.length) { colorList = defaultColorList; }
      // return { ...item, color: colorList.shift()}
      return { ...item, color: '#FFD576'}
    })
  }

  private fixRounding(value) {
    // return Number(Number(value).toFixed(2));
    const power = Math.pow(10, 2 || 0);
    return Math.round(value * power) / power;
  }

}
